import { useMemo } from 'react';

import { MAX_GAMEWEEK } from '@lib/constants';
import { usePlayers } from '@hooks/usePlayers';
import type { Player, PlayerPosition } from '@api-service/players';

const MAX_SUGGESTIONS = 3;
const SUGGESTION_GAMEWEEK_WINDOW = 3;

type UseTransferSuggestionsOptions = {
  position: PlayerPosition | null;
  /**
   * Gameweek the suggestions are anchored to (the one being viewed on the
   * pick page), so predicted points cover the same window as the rest of the
   * panel.
   */
  startGameweek: number | null;
  bank: number | null;
  excludePlayerIds?: Set<string>;
  accessToken?: string;
  enabled?: boolean;
};

export type UseTransferSuggestionsResult = {
  players: Player[];
  startGameweek: number | null;
  isLoading: boolean;
};

/**
 * Suggests replacements when transferring a player out: the top affordable
 * players in the same position, ranked by predicted points. Mirrors
 * scoreline-app's useTransferSuggestions (a thin wrapper over usePlayers)
 * rather than the heavier AI transfer recommendations endpoint, so the box is
 * a quick "best available within budget" shortlist scoped to the open slot.
 */
export const useTransferSuggestions = ({
  position,
  startGameweek,
  bank,
  excludePlayerIds,
  accessToken,
  enabled = true
}: UseTransferSuggestionsOptions): UseTransferSuggestionsResult => {
  const { data, isLoading } = usePlayers({
    enabled: enabled && startGameweek != null && position !== null,
    accessToken,
    position: position ?? undefined,
    minGameweek: startGameweek ?? undefined,
    maxGameweek:
      startGameweek != null
        ? Math.min(startGameweek + SUGGESTION_GAMEWEEK_WINDOW - 1, MAX_GAMEWEEK)
        : undefined,
    maxPrice: bank ?? undefined,
    sortBy: 'predictedPoints',
    sortDirection: 'desc'
  });

  const players = useMemo(() => {
    const all = data?.pages.flatMap((page) => page.data) ?? [];
    // usePlayers keeps the previous position's data while the next fetch is
    // in flight, so guard against suggesting a player of the wrong position
    // when the target position changes mid-flow.
    const filtered = all.filter(
      (player) =>
        player.position === position &&
        !(excludePlayerIds?.has(player.id) ?? false)
    );
    return filtered.slice(0, MAX_SUGGESTIONS);
  }, [data, excludePlayerIds, position]);

  return {
    players,
    startGameweek,
    isLoading
  };
};
